// Finance build — HTTP transport for the internal staff Income Overview: a
// whole-book money roll-up over the income ledger (every recognized income
// event across all loans within a window) plus a per-loan drill into that
// loan's events and the commissions they generated. Read-only. The hooks in
// @/hooks/portfolioAdmin/useIncomeOverview wrap this with React Query.
//
// The backend resolves the reporting window from `range`; the FE sends only
// `range` (+ from/to for custom). The resolved `range`/`from`/`to` echo back
// on the DTO.

import { api } from "@/api/client";

/** Reporting window. `custom` reveals explicit from/to date inputs. */
export type IncomeOverviewRange =
  | "month"
  | "quarter"
  | "year"
  | "lifetime"
  | "custom";

/** A money-flow group an income event rolls up into. */
export type IncomeFlowGroup =
  | "retained"
  | "outflow"
  | "passthrough"
  | "servicer"
  | "vfi";

export interface IncomeOverviewSummaryDto {
  retainedExpected: number;
  retainedReceived: number;
  outflowExpected: number;
  outflowReceived: number;
  passThroughExpected: number;
  passThroughReceived: number;
  servicerExpected: number;
  servicerReceived: number;
  vfiReceived: number;
  totalExpected: number;
  totalReceived: number;
  eventCount: number;
}

export interface IncomeBreakdownRowDto {
  key: string;
  label: string;
  group: string;
  expected: number;
  received: number;
  eventCount: number;
}

export interface IncomeOverviewLoanRowDto {
  loanId: string;
  loanNumber: string;
  retainedReceived: number;
  vfiReceived: number;
  outflowReceived: number;
  expected: number;
  received: number;
  variance: number;
  eventCount: number;
}

export interface IncomeOverviewDto {
  range: string;
  from: string | null;
  to: string | null;
  summary: IncomeOverviewSummaryDto;
  byTypePool: IncomeBreakdownRowDto[];
  byChannel: IncomeBreakdownRowDto[];
  byRecipient: IncomeBreakdownRowDto[];
  byPeriod: IncomeBreakdownRowDto[];
  byLoan: IncomeOverviewLoanRowDto[];
}

export interface EventCommissionDto {
  id: string;
  claimNumber: string;
  roleCode: string | null;
  commissionAmount: number;
  status: string;
}

export interface LoanIncomeEventDto {
  id: string;
  recognizedAt: string;
  periodStart: string | null;
  classification: string;
  group: string;
  revenuePoolCode: string | null;
  revenuePoolName: string | null;
  paymentChannel: number;
  recipientCategory: string;
  obligationTypeSnapshot: string | null;
  expectedAmount: number;
  receivedAmount: number;
  paymentId: string | null;
  commissions: EventCommissionDto[];
}

export interface LoanIncomeEventsDto {
  loanId: string;
  loanNumber: string;
  events: LoanIncomeEventDto[];
}

export interface IncomeOverviewParams {
  range: string;
  from?: string;
  to?: string;
}

export const incomeOverviewApi = {
  get: (params: IncomeOverviewParams) =>
    api.get<IncomeOverviewDto>("/portfolio-admin/income-overview", params),
  events: (loanId: string, params: IncomeOverviewParams) =>
    api.get<LoanIncomeEventsDto>(
      `/portfolio-admin/income-overview/loans/${loanId}/events`,
      params,
    ),
};
